import { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { useDeleteModal } from '~/eds/src/components/hooks/useDeleteModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { usePanelContext } from '~/eds/src/components/Panel/PanelContext';
import { Text } from '~/eds/src/components/Text/Text';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import {
  getDefaultChatContext,
  getGroupQuery,
} from '~/features/ask-anything/ask-anything-button/utils';
import { AddItemsButton } from '~/features/groups/AddItemsButton/AddItemsButton';
import { AddToGroupOverlay } from '~/features/groups/AddToGroupButton/AddToGroupOverlay';
import { GroupFormModal } from '~/features/groups/GroupFormModal';
import { WorkflowGroupNodesTable } from '~/features/groups/GroupNodesTable/GroupNodesTable';
import { EnrichedGroupNode } from '~/features/groups/helpers/NodeEnricher/NodeEnricher';
import { TreeBrowserNode } from '~/features/groups/helpers/TreeBrowser/types';
import { useTreeBrowser } from '~/features/groups/helpers/TreeBrowser/useTreeBrowser';
import {
  NodeDetailPanel,
  NodeDetailPanelId,
} from '~/features/groups/Panels/NodeDetailPanel';
import { useCanEditEntityGroups } from '~/hooks/useCanEditEntityGroups';
import { api } from '~/redux/api/createApi';
import { BatchAddGroupNodePayload } from '~/redux/api/methods/group-nodes/types';
import { UpdateGroupParams } from '~/redux/api/methods/groups/updateGroup';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';

export const messages = defineMessages({
  itemsAdded: {
    id: 'WorkflowGroupDetailPage.itemsAdded',
    defaultMessage: '{count} items added to group',
    description: 'Message when items are added to group',
  },
  itemsMoved: {
    id: 'WorkflowGroupDetailPage.itemsMoved',
    defaultMessage: '{count, plural, one {Item moved} other {# items moved}}',
    description: 'Message when items are moved to group',
  },
  itemsDeleted: {
    id: 'WorkflowGroupDetailPage.itemsDeleted',
    defaultMessage:
      '{count, plural, one {Item removed from group} other {# items removed from group}}',
    description: 'Message when items are removed from group',
  },
  deleteGroup: {
    id: 'WorkflowGroupDetailPage.deleteGroup',
    defaultMessage: 'Delete group',
    description: 'Menu action to delete the group',
  },
  groupDeleted: {
    id: 'WorkflowGroupDetailPage.groupDeleted',
    defaultMessage: 'Group deleted',
    description: 'Message when group is deleted',
  },
  deleteGroupConfirmTitle: {
    id: 'WorkflowGroupDetailPage.deleteGroupConfirmTitle',
    defaultMessage: 'Delete group',
    description: 'Title for delete group confirmation dialog',
  },
  deleteGroupConfirmMessage: {
    id: 'WorkflowGroupDetailPage.deleteGroupConfirmMessage',
    defaultMessage:
      'Are you sure you want to delete this group? This action cannot be undone.',
    description: 'Confirmation message for deleting a group',
  },
  editGroup: {
    id: 'WorkflowGroupDetailPage.editGroup',
    defaultMessage: 'Edit group',
    description: 'Menu action to edit the group',
  },
  saveGroup: {
    id: 'WorkflowGroupDetailPage.saveGroup',
    defaultMessage: 'Save',
    description: 'Button text for saving group changes',
  },
  groupUpdated: {
    id: 'WorkflowGroupDetailPage.groupUpdated',
    defaultMessage: '{groupName} has been updated.',
    description: 'Success toast message after updating a group',
  },
});

export const WorkflowGroupDetailPage = ({ groupId }: { groupId: string }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { navigate } = useRouting();
  const dispatch = useDispatch();

  const { toast } = useToast();
  const [selectedNode, setSelectedNode] =
    useState<TreeBrowserNode<EnrichedGroupNode> | null>(null);
  const [isAddingItems, setIsAddingItems] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const [deleteNode] = api.endpoints.deleteNode.useMutation();
  const [deleteGroup, { isLoading: isDeletingGroup }] =
    api.endpoints.deleteGroup.useMutation();
  const [updateGroup, { isLoading: isUpdatingGroup }] =
    api.endpoints.updateGroup.useMutation();
  const [batchAddNodes] = api.endpoints.batchAddNodes.useMutation();
  const [batchMoveNodes] = api.endpoints.batchMoveNodes.useMutation();
  const { data: groupData } = api.endpoints.getGroup.useQuery(groupId, {
    skip: !groupId,
  });

  const { hasPermission: hasEditPermission, isLoading: isLoadingPermission } =
    useCanEditEntityGroups();

  const rootNodeId = groupData?.relationships?.rootNode?.data?.id;

  useEffect(() => {
    if (groupData) {
      dispatch(
        slices.chatbot.actions.setContext([
          getDefaultChatContext(intl),
          {
            label: intl.formatMessage({
              id: 'WorkflowGroupDetailPage.thisGroupLabel',
              defaultMessage: 'This Group',
              description: 'Label for current workflow group context',
            }),
            query: getGroupQuery([String(groupData.id)]),
            title: groupData.attributes.name,
            icon: 'groups',
            primary: true,
            searchEntity: 'document',
            selectedIds: [],
            id: String(groupData.id),
            type: 'group',
          },
        ]),
      );
      dispatch(
        slices.chatbot.actions.setStatusMessage({
          message: intl.formatMessage({
            id: 'WorkflowGroupDetailPage.askAiDocumentsOnlyMessage',
            defaultMessage:
              'Search is limited to documents only. Ticket search coming soon.',
            description:
              'Info banner shown in Ask AI panel when viewing a workflow group',
          }),
        }),
      );
    }

    return () => {
      dispatch(
        slices.chatbot.actions.setContext([getDefaultChatContext(intl)]),
      );
      dispatch(slices.chatbot.actions.setStatusMessage(null));
    };
  }, [groupData]);

  const {
    treeBrowser,
    nodes,
    isLoading: isTreeLoading,
    documentDynamicFields,
    ticketDynamicFields,
    setPageSize,
  } = useTreeBrowser({
    rootNodeId,
    groupId,
  });

  const isReady = !!treeBrowser && groupData && groupId;

  const { openPanel, closePanel } = usePanelContext();

  const handleOpenDetails = (node: TreeBrowserNode<EnrichedGroupNode>) => {
    if (!node.meta.isLoadMoreNode) {
      openPanel(NodeDetailPanelId);
      setSelectedNode(node);
      openPanel(NodeDetailPanelId);
    }
  };

  const handleClosePanel = () => {
    setSelectedNode(null);
    closePanel(NodeDetailPanelId);
  };

  const refreshNodes = useCallback(
    async (nodeIds: string[]) => {
      await Promise.all(nodeIds.map((id) => treeBrowser?.loadChildren(id)));
    },
    [treeBrowser],
  );

  const handleDelete = async (nodeIds: string[]) => {
    const previousParentIds = new Set(
      nodeIds
        .map((id) => treeBrowser?.getNodeById(id)?.attributes?.parentNodeId)
        .filter(Boolean) as string[],
    );

    await Promise.all(
      nodeIds.map(async (nodeId) => {
        treeBrowser?.flagDeletingNode(nodeId);
        await deleteNode(nodeId).unwrap();
        treeBrowser?.optimisticallyDeleteNode(nodeId);
      }),
    );

    toast({
      status: 'success',
      message: formatMessage(messages.itemsDeleted, {
        count: nodeIds.length,
      }),
    });

    await refreshNodes([...previousParentIds, rootNodeId!]);
  };

  const handleMove = async (
    nodeIds: string[],
    newParentNodeId: string | null,
  ) => {
    const targetParentId = newParentNodeId ?? rootNodeId;
    if (!targetParentId) return;

    const previousParentIds = new Set(
      nodeIds
        .map((id) => treeBrowser?.getNodeById(id)?.attributes.parentNodeId)
        .filter(Boolean) as string[],
    );

    treeBrowser?.optimisticallyMoveNodes(nodeIds, targetParentId);

    await batchMoveNodes({
      nodeIds,
      newParentNodeId: targetParentId,
      rtkQueryOptions: {
        showSuccessToast: formatMessage(messages.itemsMoved, {
          count: nodeIds.length,
        }),
      },
    });

    await refreshNodes([...previousParentIds, targetParentId]);
  };

  const handleAddItems = async (payload: BatchAddGroupNodePayload) => {
    setIsAddingItems(true);
    try {
      await batchAddNodes({
        ...payload,
        rtkQueryOptions: {
          showSuccessToast: formatMessage(messages.itemsAdded, {
            count: payload.items.length,
          }),
        },
      }).unwrap();

      if (payload.parentNodeId) {
        treeBrowser?.optimisticallyMakeParent(payload.parentNodeId);

        await refreshNodes([payload.parentNodeId]);
      }
    } finally {
      setIsAddingItems(false);
    }
  };

  const handleUnlink = async (nodeIds: string[], isAllSelected?: boolean) => {
    if (isAllSelected) {
      const total =
        treeBrowser?.getNodeById(rootNodeId)?.meta.total ?? nodeIds.length;
      await batchMoveNodes({
        nodeIds: [],
        newParentNodeId: rootNodeId!,
        selectAll: true,
        rtkQueryOptions: {
          showSuccessToast: formatMessage(messages.itemsMoved, {
            count: total,
          }),
        },
      });
      await refreshNodes([rootNodeId!]);
    } else {
      await handleMove(nodeIds, rootNodeId!);
    }
  };

  const isValidDropTarget = (draggedNodeId: string, targetNodeId: string) => {
    if (!treeBrowser) return true;
    // Can't drop a node into its own descendant or itself
    return !(
      treeBrowser.isDescendantOf(targetNodeId, draggedNodeId) ||
      draggedNodeId === targetNodeId
    );
  };

  const handleDeleteGroup = async () => {
    if (!groupId) return;
    await deleteGroup({
      payload: groupId,
      rtkQueryOptions: {
        showSuccessToast: formatMessage(messages.groupDeleted),
      },
    });
    navigate('/workflow/groups');
  };

  const handleUpdateGroup = async ({ id, ...data }: UpdateGroupParams) => {
    await updateGroup({
      id,
      ...data,
      rtkQueryOptions: {
        showSuccessToast: formatMessage(messages.groupUpdated, {
          groupName: groupData?.attributes.name,
        }),
      },
    });
  };

  const [deleteGroupConfirmModal, showDeleteGroupModal] = useDeleteModal({
    title: formatMessage(messages.deleteGroupConfirmTitle),
    children: <Text>{formatMessage(messages.deleteGroupConfirmMessage)}</Text>,
    isDeleting: isDeletingGroup,
    onDelete: handleDeleteGroup,
  });

  return (
    <PageLayout
      title={groupData?.attributes?.name ?? ''}
      headerCalloutContent={
        groupId &&
        hasEditPermission && (
          <>
            <AddItemsButton
              groupId={groupId}
              rootNodeId={rootNodeId}
              onItemsAdded={handleAddItems}
              onLoadAllNodes={async () => {
                await treeBrowser?.loadAllNodes();
                return treeBrowser?.getAllNodes() ?? [];
              }}
            />
            <ActionsMenu
              name="group-actions"
              actions={[
                {
                  label: formatMessage(messages.editGroup),
                  value: 'edit',
                  icon: 'edit',
                  onClick: () => setIsEditModalVisible(true),
                },
                {
                  label: formatMessage(messages.deleteGroup),
                  value: 'delete',
                  icon: 'trash',
                  onClick: showDeleteGroupModal,
                },
              ]}
            />
          </>
        )
      }
    >
      <AddToGroupOverlay isVisible={isAddingItems} />

      {isReady && (
        <WorkflowGroupNodesTable
          nodes={nodes}
          rootNodeId={rootNodeId}
          totalCount={treeBrowser?.getNodeById(rootNodeId)?.meta.total}
          documentDynamicFields={documentDynamicFields ?? {}}
          ticketDynamicFields={ticketDynamicFields ?? {}}
          onCollapse={(node) => treeBrowser?.collapse(node.id)}
          hasEditPermission={hasEditPermission}
          onExpand={(node) => treeBrowser?.expand(node.id)}
          onRowClick={handleOpenDetails}
          isLoading={isTreeLoading || isLoadingPermission}
          isValidDropTarget={isValidDropTarget}
          onMove={handleMove}
          onUnlink={handleUnlink}
          onDelete={handleDelete}
          getDescendants={treeBrowser?.getDescendants}
          onLoadAllNodes={async () => {
            await treeBrowser?.loadAllNodes();
            return treeBrowser?.getAllNodes();
          }}
          onLoadMore={async (nodeId) => {
            await treeBrowser?.loadMore(nodeId);
          }}
          onPageSizeChange={setPageSize}
        />
      )}

      <NodeDetailPanel
        node={selectedNode}
        onClose={handleClosePanel}
        currentGroup={groupId}
      />

      {deleteGroupConfirmModal}

      <GroupFormModal
        isVisible={isEditModalVisible}
        onHide={() => setIsEditModalVisible(false)}
        onSubmit={(data) => handleUpdateGroup({ id: groupId ?? '', ...data })}
        title={formatMessage(messages.editGroup)}
        submitButtonText={formatMessage(messages.saveGroup)}
        initialName={groupData?.attributes?.name}
        initialNote={groupData?.attributes?.note}
        isLoading={isUpdatingGroup}
      />
    </PageLayout>
  );
};

export default () => {
  // Make sure that we unmount/remount the component when the groupId changes
  const { groupId } = useParams<{ groupId: string }>();

  return <WorkflowGroupDetailPage key={groupId} groupId={groupId ?? ''} />;
};
